import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/components/auth/AuthProvider';
import { toast } from 'sonner';

export interface OutreachRequest {
  id: string;
  user_id: string;
  nursing_home_id: string;
  nursing_home_name: string;
  nursing_home_email: string | null;
  status: string;
  email_sent_at: string | null;
  opened_at: string | null;
  replied_at: string | null;
  meeting_scheduled_at: string | null;
  meeting_date: string | null;
  notes: string | null;
  user_message: string | null;
  created_at: string;
  updated_at: string;
}

export function useOutreach() {
  const { user } = useAuth();
  const [requests, setRequests] = useState<OutreachRequest[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchRequests = useCallback(async () => {
    if (!user) return;
    setIsLoading(true);
    const { data, error } = await supabase
      .from('outreach_requests')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching outreach requests:', error);
    } else {
      setRequests((data || []) as OutreachRequest[]);
    }
    setIsLoading(false);
  }, [user]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const sendOutreach = async (nursingHomeId: string, nursingHomeName: string, userMessage?: string) => {
    if (!user) {
      toast.error('Please log in to send outreach requests');
      return null;
    }

    try {
      // Check if already sent for this home
      const existing = requests.find(
        r => r.nursing_home_id === nursingHomeId && r.status !== 'no_email'
      );
      if (existing) {
        toast.info('You already have an outreach request for this nursing home');
        return existing;
      }

      // Create the outreach request record
      const { data: newRequest, error: insertError } = await supabase
        .from('outreach_requests')
        .insert({
          user_id: user.id,
          nursing_home_id: nursingHomeId,
          nursing_home_name: nursingHomeName,
          status: 'pending',
          user_message: userMessage || null,
        } as any)
        .select()
        .single();

      if (insertError) throw insertError;

      // Trigger the edge function to send the email
      const { data: emailResult, error: emailError } = await supabase.functions.invoke(
        'send-outreach-email',
        {
          body: JSON.stringify({
            outreach_request_id: newRequest.id,
            nursing_home_id: nursingHomeId,
            nursing_home_name: nursingHomeName,
            user_message: userMessage,
          }),
        }
      );

      if (emailError) throw emailError;

      if (emailResult?.status === 'no_email') {
        toast.warning(`No email address available for ${nursingHomeName}`, {
          description: "We'll try other methods to reach them.",
        });
      } else {
        toast.success(`Outreach email sent to ${nursingHomeName}!`, {
          description: "You've been CC'd — check your inbox.",
        });
      }

      await fetchRequests();
      return newRequest;
    } catch (error: any) {
      console.error('Error sending outreach:', error);
      toast.error('Failed to send outreach', { description: error.message });
      return null;
    }
  };

  const sendBulkOutreach = async (
    homes: Array<{ id: string; name: string }>,
    userMessage?: string
  ) => {
    const results = [];
    for (const home of homes) {
      const result = await sendOutreach(home.id, home.name, userMessage);
      results.push(result);
    }
    return results;
  };

  return {
    requests,
    isLoading,
    sendOutreach,
    sendBulkOutreach,
    refetch: fetchRequests,
  };
}
